/**
 * Стоимость ресурсов графика по неделям рядом с планом оплат бюджета (РС-12): ФОТ, техника, материалы
 * по назначениям работам графика против бюджет-графика. Считает график ресурсов, здесь только сведение.
 */
import type { ResourceBudgetView } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { date, tengeShort } from '../../lib/format';
import { ErrorBox, Spinner } from '../../ui/feedback';
import { Panel } from '../../ui/layout';

export function ResourceCostPanel({ projectId, versionId }: { projectId: string; versionId: string }) {
  const q = useQuery({
    queryKey: ['budget', 'resource-budget', projectId, versionId],
    queryFn: () =>
      api<ResourceBudgetView>(`/projects/${projectId}/resource-budget`, { query: { versionId } }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const v = q.data;
  // Нет ни назначений ресурсов, ни плана оплат по неделям — показывать нечего
  if (!v.weeks.length) return null;
  const t = v.totals;
  return (
    <Panel
      title="Стоимость ресурсов графика по неделям"
      meta={`РС-12: ресурсы ${tengeShort(t.resources)} против плана оплат ${tengeShort(t.plan)}`}
      bodyClassName="p-0"
    >
      {v.missingRates.length ? (
        <p className="border-b border-border px-4 py-2 text-sm text-warning">
          Нет ставки на дату: {v.missingRates.join(', ')} — их стоимость не учтена.
        </p>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted">
              <th className="px-4 py-2 font-medium">Неделя с</th>
              <th className="px-3 py-2 text-right font-medium">ФОТ</th>
              <th className="px-3 py-2 text-right font-medium">Техника</th>
              <th className="px-3 py-2 text-right font-medium">Материалы</th>
              <th className="px-3 py-2 text-right font-medium">Ресурсы</th>
              <th className="px-3 py-2 text-right font-medium">План оплат</th>
              <th className="px-4 py-2 text-right font-medium">Разница</th>
            </tr>
          </thead>
          <tbody>
            {v.weeks.map((w, i) => (
              <tr key={w} className="border-b border-border last:border-0">
                <td className="px-4 py-2 whitespace-nowrap">{date(w)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{tengeShort(v.labor[i])}</td>
                <td className="px-3 py-2 text-right tabular-nums">{tengeShort(v.equipment[i])}</td>
                <td className="px-3 py-2 text-right tabular-nums">{tengeShort(v.material[i])}</td>
                <td className="px-3 py-2 text-right tabular-nums">{tengeShort(v.resources[i])}</td>
                <td className="px-3 py-2 text-right tabular-nums">{tengeShort(v.plan[i])}</td>
                <td className="px-4 py-2 text-right tabular-nums">{tengeShort(v.diff[i])}</td>
              </tr>
            ))}
            <tr className="font-medium">
              <td className="px-4 py-2">Всего</td>
              <td className="px-3 py-2 text-right tabular-nums">{tengeShort(t.labor)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{tengeShort(t.equipment)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{tengeShort(t.material)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{tengeShort(t.resources)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{tengeShort(t.plan)}</td>
              <td className="px-4 py-2 text-right tabular-nums">{tengeShort(t.diff)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
